import { withTheme, withStyles } from '@material-ui/core/styles';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import clsx from 'clsx';
import numeral from 'numeral';
import { LegendOrdinal, LegendQuantile, LegendThreshold, LegendLinear } from '@visx/legend';
import * as d3 from 'd3';
import debounce from 'lodash.debounce';
import { v4 } from 'uuid';
import { Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { Axis, Orientation } from '@visx/axis';
import { Brush } from '@visx/brush';
import { PatternLines } from '@visx/pattern';
import Divider from '@material-ui/core/Divider';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }

  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();

  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];

  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;

      var F = function () {};

      return {
        s: F,
        n: function () {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function (e) {
          throw e;
        },
        f: F
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var normalCompletion = true,
      didErr = false,
      err;
  return {
    s: function () {
      it = it.call(o);
    },
    n: function () {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function (e) {
      didErr = true;
      err = e;
    },
    f: function () {
      try {
        if (!normalCompletion && it.return != null) it.return();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

var LEGEND_WIDTH = 250;
var RFPROP_CHART_HEIGHT = 100;
var RF_PROP_COL_MEAN = 2;
var RF_PROP_COL_COUNT = 1;
var PATTERN_ID = 'brush_pattern';

var styles = function styles(theme) {
  return {
    legend: {
      position: 'absolute',
      right: theme.spacing(1),
      bottom: theme.spacing(1) + 20
      /* map attribution height */
      ,
      width: LEGEND_WIDTH,
      minHeight: 50,
      padding: theme.spacing(1, 1, 0.5, 1)
    },
    svgDataBox: {
      width: '100%'
    },
    listItemIcon: {
      minWidth: 0
    },
    divider: {
      // these are split because of variant="middle"
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    markerLegend: {
      '& div': {
        borderRadius: '50%'
      }
    }
  };
};

var Legend = /*#__PURE__*/function (_React$PureComponent) {
  _inherits(Legend, _React$PureComponent);

  var _super = _createSuper(Legend);

  function Legend(props) {
    var _this;

    _classCallCheck(this, Legend);

    _this = _super.call(this, props);
    var theme = props.theme;
    _this.state = {
      svgId: "svg-".concat(v4()),
      brushStyle: {
        fill: "url(#".concat(PATTERN_ID, ")"),
        stroke: theme.palette.text.primary
      }
    };
    _this.margins = {
      top: theme.spacing(1),
      left: theme.spacing(2),
      right: theme.spacing(2),
      bottom: theme.spacing(3)
    }; // BIND

    _this.tickLabelProps = _this.tickLabelProps.bind(_assertThisInitialized(_this));
    _this.onBrush = _this.onBrush.bind(_assertThisInitialized(_this)); // DEBOUNCE

    _this.onBrushDebounced = debounce(_this.onBrush, 250, {
      trailing: true,
      leading: false
    }); // CONSTANTS

    _this._legendFormat = '0,0';
    _this._markerColors = d3.scaleOrdinal().domain(['Wifi Transmitter', 'Cell Tower']).range([[173, 221, 142, 200], [254, 196, 79, 200]]);
    return _this;
  }

  _createClass(Legend, [{
    key: "tickLabelProps",
    value: function tickLabelProps() {
      var theme = this.props.theme;
      return {
        fill: theme.palette.text.primary,
        fontSize: 10,
        fontFamily: theme.typography.fontFamily,
        textAnchor: 'middle'
      };
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.onBrushDebounced.cancel();
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      this.componentDidUpdate({});
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$props = this.props,
          selectedDoc = _this$props.selectedDoc,
          theme = _this$props.theme;
      var prevSelectedDoc = prevProps.selectedDoc,
          prevTheme = prevProps.theme;

      if (!selectedDoc) {
        if (this.state.db) {
          this.setState({
            xMax: null,
            yMax: null,
            yScale: null,
            xScale: null,
            data: null,
            db: null
          });
        }
      } else if (selectedDoc && selectedDoc !== prevSelectedDoc) {
        var data = {};
        var rf = selectedDoc.rf_prop;

        var _iterator = _createForOfIteratorHelper(rf),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var _data$_db;

            var arr = _step.value;

            var _db = Math.round(arr[RF_PROP_COL_MEAN]);

            data[_db] = (_data$_db = data[_db]) !== null && _data$_db !== void 0 ? _data$_db : 0;
            data[_db] += arr[RF_PROP_COL_COUNT];
          } // const quantiles = d3.scaleQuantile().domain(Object.values(data)).range([0, 0.25, 0.5, 0.75]).quantiles();

        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }

        var db = Object.keys(data); // for (const d of db) {
        //   if (data[d] <= quantiles[0]) {
        //     delete data[d];
        //   }
        // }

        var counts = Object.values(data);
        db = Object.keys(data).map(function (d) {
          return +d;
        }).sort(function (a, b) {
          if (a === b) return 0;
          if (a > b) return -1;
          return 1;
        });
        var yMax = RFPROP_CHART_HEIGHT - this.margins.top - this.margins.bottom;
        var xMax = LEGEND_WIDTH - this.margins.left - this.margins.right - theme.spacing(2);
        var yScale = d3.scaleLinear().domain([d3.max(counts), 0]).range([0, yMax]).nice();
        var xScale = d3.scaleBand().domain(db).range([0, xMax]).paddingInner(0.1);
        this.setState({
          xMax: xMax,
          yMax: yMax,
          yScale: yScale,
          xScale: xScale,
          data: data,
          db: db
        });
      }

      if (theme !== prevTheme) {
        this.setState({
          brushStyle: {
            fill: "url(#".concat(PATTERN_ID, ")"),
            stroke: theme.palette.text.primary
          }
        });
      }
    }
  }, {
    key: "onBrush",
    value: function onBrush(domain) {
      // throttled @ 250ms
      this.props.onFilterRfProp(domain);
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          theme = _this$props2.theme,
          classes = _this$props2.classes,
          hideAggLayers = _this$props2.hideAggLayers,
          onHideAggLayersChanged = _this$props2.onHideAggLayersChanged,
          radioHexLegendScale = _this$props2.radioHexLegendScale,
          radioHexData = _this$props2.radioHexData,
          rfpropScale = _this$props2.rfpropScale,
          rfpropSteps = _this$props2.rfpropSteps,
          rfpropResized = _this$props2.rfpropResized,
          radioPointData = _this$props2.radioPointData;
      var _this$state = this.state,
          yScale = _this$state.yScale,
          xScale = _this$state.xScale,
          data = _this$state.data,
          db = _this$state.db,
          xMax = _this$state.xMax,
          yMax = _this$state.yMax,
          brushStyle = _this$state.brushStyle;
      var renderHexLegend = radioHexLegendScale && radioHexData && radioHexData.length > 0;
      var renderPointLegend = radioPointData && radioPointData.length > 0;
      var scaleType = !radioHexLegendScale ? '' : radioHexLegendScale.TYPE.charAt(0) + radioHexLegendScale.TYPE.toLowerCase().substring(1);
      if (!renderHexLegend && !db && !rfpropScale && !radioPointData) return null;
      return /*#__PURE__*/React.createElement(Paper, {
        className: clsx('noselect', classes.legend)
      }, !hideAggLayers && renderPointLegend && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LegendOrdinal, {
        className: classes.markerLegend,
        scale: this._markerColors,
        fill: function fill(_ref) {
          var value = _ref.value;
          return d3.color("rgb(".concat(value[0], ",").concat(value[1], ",").concat(value[2], ")")).formatHex();
        }
      }), (renderHexLegend || rfpropScale) && /*#__PURE__*/React.createElement(Divider, {
        variant: "middle",
        className: classes.divider
      })), !hideAggLayers && renderHexLegend && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        component: "div"
      }, "Density (".concat(scaleType, ")")), radioHexLegendScale.TYPE === 'QUANTILE' && /*#__PURE__*/React.createElement(LegendQuantile, {
        labelFormat: function labelFormat(d) {
          return numeral(d).format(_this2._legendFormat);
        },
        scale: radioHexLegendScale,
        fill: function fill(_ref2) {
          var value = _ref2.value;
          return d3.color("rgb(".concat(value[0], ",").concat(value[1], ",").concat(value[2], ")")).formatHex();
        }
      }), radioHexLegendScale.TYPE === 'ORDINAL' && /*#__PURE__*/React.createElement(LegendOrdinal, {
        scale: radioHexLegendScale,
        fill: function fill(_ref3) {
          var value = _ref3.value;
          return d3.color("rgb(".concat(value[0], ",").concat(value[1], ",").concat(value[2], ")")).formatHex();
        },
        labelFormat: function labelFormat(d) {
          return numeral(d).format(_this2._legendFormat);
        }
      }), radioHexLegendScale.TYPE === 'THRESHOLD' && /*#__PURE__*/React.createElement(LegendThreshold, {
        scale: radioHexLegendScale,
        fill: function fill(_ref4) {
          var value = _ref4.value;
          if (!value) return '#000000';
          return d3.color("rgb(".concat(value[0], ",").concat(value[1], ",").concat(value[2], ")")).formatHex();
        },
        labelFormat: function labelFormat(d) {
          return numeral(d).format(_this2._legendFormat);
        }
      }), rfpropScale && /*#__PURE__*/React.createElement(Divider, {
        variant: "middle",
        className: classes.divider
      })), rfpropScale && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1"
      }, "Signal Strength (Mean".concat(rfpropResized ? '*' : '', ")")), /*#__PURE__*/React.createElement(LegendLinear, {
        steps: rfpropSteps,
        scale: rfpropScale,
        labelFormat: function labelFormat(d) {
          return numeral(d).format('0[.]0');
        }
      }), db && /*#__PURE__*/React.createElement(Divider, {
        variant: "middle",
        className: classes.divider
      })), db && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        variant: "body1"
      }, "Signal Distribution (dBm".concat(rfpropResized ? '*' : '', ")")), /*#__PURE__*/React.createElement("svg", {
        width: LEGEND_WIDTH - theme.spacing(2),
        height: RFPROP_CHART_HEIGHT
      }, /*#__PURE__*/React.createElement(Group, {
        top: this.margins.top,
        left: this.margins.left
      }, db.map(function (d) {
        var barWidth = xScale.bandwidth();
        var barHeight = yMax - yScale(data[d]);

        if (barHeight > 1.0) {
          barHeight = Math.max(barHeight, 3);
        }

        var barX = xScale(d);
        var barY = yMax - barHeight;
        return /*#__PURE__*/React.createElement(Bar, {
          id: d,
          key: "bar-".concat(d),
          x: barX,
          y: barY,
          width: barWidth,
          height: barHeight,
          fill: theme.palette.secondary.main,
          onClick: function onClick() {}
        });
      }), /*#__PURE__*/React.createElement(PatternLines, {
        id: PATTERN_ID,
        height: 8,
        width: 8,
        stroke: theme.palette.secondary.main,
        strokeWidth: 1,
        orientation: ['diagonal']
      }), /*#__PURE__*/React.createElement(Brush, {
        xScale: xScale,
        yScale: yScale,
        width: xMax,
        height: yMax,
        margin: this.margins,
        selectedBoxStyle: brushStyle,
        onChange: this.onBrushDebounced,
        handleSize: 8
      })), /*#__PURE__*/React.createElement(Axis // tickFormat={(d) => numeral(d).format('0.00')}
      , {
        orientation: Orientation.bottom,
        left: this.margins.left,
        top: RFPROP_CHART_HEIGHT - this.margins.bottom,
        scale: xScale,
        tickStroke: theme.palette.text.primary,
        stroke: theme.palette.text.primary,
        tickLabelProps: this.tickLabelProps,
        numTicks: Math.min(db.length, 6)
      })), /*#__PURE__*/React.createElement(FormControl, {
        component: "fieldset"
      }, /*#__PURE__*/React.createElement(FormControlLabel, {
        control: /*#__PURE__*/React.createElement(Checkbox, {
          checked: hideAggLayers,
          onChange: function onChange(e) {
            onHideAggLayersChanged(e.target.checked);
          },
          name: "hideAggs"
        }),
        label: "Show only selected"
      }))));
    }
  }]);

  return Legend;
}(React.PureComponent);

var mapStateToProps = function
  /* state */
mapStateToProps() {
  return {// mapZoom: state.app.mapZoom
  };
};

var mapDispatchToProps = function
  /* dispatch */
mapDispatchToProps() {
  return {};
};

Legend.propTypes = {
  // ui / ux
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  // data
  selectedDoc: PropTypes.object,
  hideAggLayers: PropTypes.bool,
  radioHexLegendScale: PropTypes.func,
  radioHexData: PropTypes.array,
  radioPointData: PropTypes.array,
  rfpropScale: PropTypes.func,
  rfpropSteps: PropTypes.number,
  onFilterRfProp: PropTypes.func,
  rfpropResized: PropTypes.bool,
  // callbacks
  onHideAggLayersChanged: PropTypes.func
};
Legend.defaultProps = {};
var withRedux = connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: false
});
var Legend$1 = withRedux(withTheme(withStyles(styles)(Legend)));

export { Legend$1 as Legend };
